import {
  GLOBAL_CONSTANTS,
  NEWS_CONSTANTS,
  QUERY_CONSTANTS,
} from '@/constants';

import {
  JOB_CONSTANTS,
} from '@/constants';

import {
  DateTime,
} from 'luxon';

import {
  getLuxonDate,
} from '@/composables/date.js';

import {
  aggregator_user_data_repository,
  business_alert_repository,
  query_repository,
  search_repository,
} from '@/repositories';

const HIERARCHICAL_LEVELS = {
  DIRECTORS_IDS: [1, 3, 4],
  MANAGERS_IDS: [2],
  EXPERTS_IDS: [5],
};

const TAG_GROUP_IDCC = 12;
const TAG_CHAMPIONS_OF_CUSTOMER_SERVICE = 333;

const state = () => ({
  account: null,
  account_is_inactive: null,
  account_with_aggregated_data: null,
  categorized_chart_contacts: {},
  chart_contact_list: null,
  chart_department_id_list_search: [],
  chart_department_option_list: [],
  chart_job_name_option_list: [],
  chart_job_name_search: '',
  chart_view: 'grid',
  latest_news_list: null,
  loading_search_news_with_criteria: false,
  most_recent_chart_contact_list: [],
  news_criteria_limit: GLOBAL_CONSTANTS.ACCOUNT_NEWS_LIMIT,
  news_criteria_offset: 0,
  news_criteria_publication_date_max: null,
  news_criteria_publication_date_min: undefined,
  news_criteria_quick_search: '',
  news_list_with_criteria: [],
  news_criteria_tag_id_list: NEWS_CONSTANTS.ELIGIBLE_NEWS_TYPE_ID_LIST_FOR_ACCOUNT,
  primary_tag_list: [],
  reach_end_search_news_with_criteria: false,
  searched_chart_contacts: {},
  secondary_tag_list: [],
  smart_score_for_account: undefined,
});

const getters = {
  account: (state) => state.account,
  account_is_inactive: (state) => state.account_is_inactive,
  account_with_aggregated_data: (state) => state.account_with_aggregated_data,
  chart_contact_list: (state) => state.chart_contact_list,
  chart_department_id_list_search: (state) => state.chart_department_id_list_search,
  chart_department_option_list: (state) => state.chart_department_option_list,
  chart_job_name_option_id_list: (state) => state.chart_job_name_option_id_list,
  chart_job_name_search: (state) => state.chart_job_name_search,
  chart_view: (state) => state.chart_view,
  latest_news_list: (state) => state.latest_news_list,
  loading_search_news_with_criteria: (state) => state.loading_search_news_with_criteria,
  most_recent_chart_contact_list: (state) => state.most_recent_chart_contact_list,
  news_criteria_limit: (state) => state.news_criteria_limit,
  news_criteria_offset: (state) => state.news_criteria_offset,
  news_criteria_publication_date_max: (state) => state.news_criteria_publication_date_max,
  news_criteria_publication_date_min: (state) => state.news_criteria_publication_date_min,
  news_criteria_quick_search: (state) => state.news_criteria_quick_search,
  news_criteria_tag_id_list: (state) => state.news_criteria_tag_id_list,
  news_list_with_criteria: (state) => state.news_list_with_criteria,
  primary_tag_list: (state) => state.primary_tag_list,
  reach_end_search_news_with_criteria: (state) => state.reach_end_search_news_with_criteria,
  searched_chart_contacts: (state) => state.searched_chart_contacts,
  secondary_tag_list: (state) => state.secondary_tag_list,
  smart_score_for_account: (state) => state.smart_score_for_account,
};

const actions = {
  async getAccountById({ commit }, account_id) {
    console.time('AccountStore - getAccountById');

    const account = await search_repository.getAccountById(account_id);

    commit('set_account', account);

    commit('set_account_is_inactive', account?.active !== true);

    const primary_tag_list = account?.tag_list
      ?.filter((tag) => GLOBAL_CONSTANTS.ACCOUNT_PRIMARY_TAG_ID_LIST.includes(tag.id))
      ?.map((tag) => tag.list)
      .flat() || [];

    const secondary_tag_list_flat = account?.tag_list
      ?.filter((tag) => !(GLOBAL_CONSTANTS.ACCOUNT_PRIMARY_TAG_ID_LIST.concat(TAG_GROUP_IDCC))
        .includes(tag.id))
      ?.map((tag) => tag.list.map((item) => {
        let name = item.name;

        if (item.id === TAG_CHAMPIONS_OF_CUSTOMER_SERVICE) {
          name = item.name.replace('_', ' ');
        }

        return {
          ...item,
          name,
        };
      }))
      .flat() || [];

    const seen_secondary_tag_names = new Set();

    const secondary_tag_list = secondary_tag_list_flat.filter((item) => {
      if (seen_secondary_tag_names.has(item.name)) {
        return false;
      }

      seen_secondary_tag_names.add(item.name);

      return true;
    });

    commit('set_primary_tag_list', primary_tag_list);
    commit('set_secondary_tag_list', secondary_tag_list);

    console.timeEnd('AccountStore - getAccountById');

    return account;
  },
  async getAccountSegments({ rootState }, criteria) {
    const updated_criteria = {
      query_type_id_list: [
        QUERY_CONSTANTS.TYPES.SEGMENT_ACCOUNT,
        QUERY_CONSTANTS.TYPES.CLIENTS,
        QUERY_CONSTANTS.TYPES.PROSPECTS,
        QUERY_CONSTANTS.TYPES.COMPETITORS,
        QUERY_CONSTANTS.TYPES.KEY_ACCOUNTS,
      ],
      sort: ['-modification_date'],
      ...criteria,
    };

    const {
      query_list,
    } = await query_repository.searchUserQueryList(
      rootState.user.token_data.user_id,
      updated_criteria
    );

    return query_list;
  },
  async searchInteractions(_, input) {
    const {
      enriched_interactions_list,
    } = await aggregator_user_data_repository.searchInteractions(input);

    return enriched_interactions_list || [];
  },
  async getAccountWithAggregatedData({ commit }, criteria) {
    const result = await aggregator_user_data_repository.searchAccounts({
      ...criteria,
      matched_queries_query_type_id_list: [
        QUERY_CONSTANTS.TYPES.PROSPECTING_ACCOUNT,
      ],
    });

    commit('set_account_with_aggregated_data', result?.company_list?.at(0) || null);

    return result?.company_list?.at(0) || null;
  },
  async searchNews(_, params) {
    const response = await business_alert_repository.searchNews(params);

    return response.news_list;
  },
  async searchNewsWithCriteria({ commit, state }) {
    commit('set_loading_search_news_with_criteria', true);

    if (state.news_criteria_offset === 0) {
      commit('set_news_list_with_criteria', []);
    }

    const data = {
      company_id_list: [state.account.id],
      limit: state.news_criteria_limit,
      news_tag_id_list: state.news_criteria_tag_id_list,
      offset: state.news_criteria_offset,
      sort: ['-publication_date'],
    };

    if (state.news_criteria_publication_date_min) {
      data.news_publication_date_min = getLuxonDate(state.news_criteria_publication_date_min, {zone: 'utc'})
        .startOf('day')
        .toISO();
    }

    if (state.news_criteria_quick_search.length > 0) {
      data.quick_search = state.news_criteria_quick_search;
    }

    try {
      const response = await business_alert_repository.searchNews(data);

      let news_list;

      if (state.news_criteria_offset === 0) {
        news_list = response.news_list;
      } else {
        news_list = state.news_list_with_criteria.concat(response.news_list);
      }

      if (response.news_list.length < state.news_criteria_limit) {
        commit('set_reach_end_search_news_with_criteria', true);
      }

      commit('set_news_criteria_offset', state.news_criteria_offset + GLOBAL_CONSTANTS.ACCOUNT_NEWS_LIMIT);
      commit('set_news_list_with_criteria', news_list);
      commit('set_loading_search_news_with_criteria', false);
    } catch (error) {
      console.error('searchNewsWithCriteria', error);

      commit('set_loading_search_news_with_criteria', false);
    }

    return null;
  },
  async getActiveConsultedContacts({ rootState }, input) {
    const {
      account_id,
      limit,
    } = input;

    const result_1 = await query_repository.searchUserQueryList(
      rootState.user.token_data.user_id,
      {
        query_type_id_list: [
          QUERY_CONSTANTS.TYPES.CONSULTED_CONTACT,
        ],
      }
    );

    const query_id = result_1.query_list.at(0).id;

    const result_2 = await search_repository.getContactsActive({
      company_id_list: [account_id],

      query_id_list: [query_id],
      limit,
    });

    return result_2?.contact_list || [];
  },
  async getChartContacts({ commit, dispatch }, input) {
    console.time('AccountStore - getChartContacts');
    const {
      account_id,
    } = input;

    const result = await search_repository.getChartContacts({
      company_id_list: [account_id],
      limit: -1,
    });

    if (result?.contact_list?.length <= 6) {
      commit('set_chart_view', 'list');
    }

    const chart_contact_list = [];
    const most_recent_chart_contact_list = [];

    result?.contact_list
      .forEach((contact) => {
        const first_job = contact.job_list?.at(0);
        const all_job_type_id_list = contact.job_list
          ?.flatMap((job) => job?.job_type_list || [])
          ?.map((job_type) => job_type?.id)
          ?.filter(Boolean) || [];
        const job_type_id_list = [...new Set(all_job_type_id_list)];
        const main_job = contact.job_list?.find((job) => job.main) || first_job;

        let job_category = 'other';

        if (main_job?.board_member) {
          job_category = 'codir';
        } else if (main_job?.chief_officer) {
          job_category = 'c_level';
        }

        const job_hierarchical_level_id = main_job?.hierarchical_level?.id;
        let job_hierarchical_level = '';

        if (HIERARCHICAL_LEVELS.DIRECTORS_IDS.includes(job_hierarchical_level_id)) {
          job_hierarchical_level = 'director';
        } else if (HIERARCHICAL_LEVELS.MANAGERS_IDS.includes(job_hierarchical_level_id)) {
          job_hierarchical_level = 'manager';
        } else if (HIERARCHICAL_LEVELS.EXPERTS_IDS.includes(job_hierarchical_level_id)) {
          job_hierarchical_level = 'expert';
        } else {
          job_hierarchical_level = 'other';
        }

        const enriched_contact = {
          ...contact,
          department: main_job?.office?.address?.department,
          job_type_id_list,
          job_category,
          job_hierarchical_level,
          job_name: main_job?.name,
          main_job,
        };

        const six_months_ago = DateTime
          .now()
          .minus({
            months: 6,
          });

        const start_date = DateTime.fromSQL(main_job?.start_date);

        if (start_date.isValid && start_date > six_months_ago) {
          most_recent_chart_contact_list.push(enriched_contact);
        }

        chart_contact_list.push(enriched_contact);
      });

    commit('set_chart_contact_list', chart_contact_list);

    commit('set_most_recent_chart_contact_list',
      most_recent_chart_contact_list
        .sort((a, b) => (b.main_job.start_date).localeCompare(a.main_job.start_date))
    );

    dispatch('categorizeChartContactsAndPopulateOptionLists');

    console.timeEnd('AccountStore - getChartContacts');

    return chart_contact_list;
  },
  async categorizeChartContactsAndPopulateOptionLists({ commit, state }) {
    const categorized_chart_contacts = {
      governance_contact_list: [],
      general_management_contact_list: [],
      business_unit_management_contact_list: [],
      strategic_management_contact_list: [],
      finance_administrative_contact_list: [],
      human_resources_contact_list: [],
      environment_contact_list: [],
      legal_contact_list: [],
      real_estate_contact_list: [],
      purchasing_contact_list: [],
      marketing_contact_list: [],
      digital_contact_list: [],
      communication_contact_list: [],
      sales_contact_list: [],
      distribution_contact_list: [],
      artistic_contact_list: [],
      it_contact_list: [],
      research_contact_list: [],
      production_contact_list: [],
      supply_chain_contact_list: [],
      consulting_contact_list: [],
      finance_contact_list: [],
      media_contact_list: [],
      cse_contact_list: [],
      public_service_contact_list: [],
      assistant_contact_list: [],
      other_contact_list: [],
      executive_committee_contact_list: [],
    };

    const chart_department_option_list = [];
    const chart_job_name_option_id_list = [];

    const sorted_contact_list = [...state.chart_contact_list]
      .sort((a, b) => {
        const a_name = `${a.last_name} ${a.first_name}`;
        const b_name = `${b.last_name} ${b.first_name}`;

        return a_name.localeCompare(b_name, undefined, { sensitivity: 'base' });
      });

    const codir_contact_list = sorted_contact_list
      .filter((contact) => contact.job_category === 'codir');

    const c_level_contact_list = sorted_contact_list
      .filter((contact) => contact.job_category === 'c_level');

    const other_contact_list = sorted_contact_list
      .filter((contact) => contact.job_category === 'other');

    [
      ...codir_contact_list,
      ...c_level_contact_list,
      ...other_contact_list,
    ]
      .forEach((contact) => {
        if (
          contact.job_type_id_list.includes(
            JOB_CONSTANTS.JOB_CLASS_LIST
              ?.at(0)
              ?.job_type_list
              ?.at(0)
              ?.id
          )
        ) {
          categorized_chart_contacts.governance_contact_list.push(contact);
        }

        if (
          contact.job_type_id_list.includes(
            JOB_CONSTANTS.JOB_CLASS_LIST
              ?.at(1)
              ?.job_type_list
              ?.at(0)
              ?.id
          )
        ) {
          categorized_chart_contacts.general_management_contact_list.push(contact);
        }

        if (
          contact.job_type_id_list.includes(
            JOB_CONSTANTS.JOB_CLASS_LIST
              ?.at(1)
              ?.job_type_list
              ?.at(1)
              ?.id
          )
        ) {
          categorized_chart_contacts.business_unit_management_contact_list.push(contact);
        }

        if (
          contact.job_type_id_list.includes(
            JOB_CONSTANTS.JOB_CLASS_LIST
              ?.at(1)
              ?.job_type_list
              ?.at(2)
              ?.id
          )
        ) {
          categorized_chart_contacts.strategic_management_contact_list.push(contact);
        }

        if (
          contact.job_type_id_list.includes(
            JOB_CONSTANTS.JOB_CLASS_LIST
              ?.at(2)
              ?.job_type_list
              ?.at(0)
              ?.id
          )
        ) {
          categorized_chart_contacts.finance_administrative_contact_list.push(contact);
        }

        if (
          contact.job_type_id_list.includes(
            JOB_CONSTANTS.JOB_CLASS_LIST
              ?.at(2)
              ?.job_type_list
              ?.at(1)
              ?.id
          )
        ) {
          categorized_chart_contacts.human_resources_contact_list.push(contact);
        }

        if (
          contact.job_type_id_list.includes(
            JOB_CONSTANTS.JOB_CLASS_LIST
              ?.at(2)
              ?.job_type_list
              ?.at(2)
              ?.id
          )
        ) {
          categorized_chart_contacts.environment_contact_list.push(contact);
        }

        if (
          contact.job_type_id_list.includes(
            JOB_CONSTANTS.JOB_CLASS_LIST
              ?.at(2)
              ?.job_type_list
              ?.at(3)
              ?.id
          )
        ) {
          categorized_chart_contacts.legal_contact_list.push(contact);
        }

        if (
          contact.job_type_id_list.includes(
            JOB_CONSTANTS.JOB_CLASS_LIST
              ?.at(2)
              ?.job_type_list
              ?.at(4)
              ?.id
          )
        ) {
          categorized_chart_contacts.real_estate_contact_list.push(contact);
        }

        if (
          contact.job_type_id_list.includes(
            JOB_CONSTANTS.JOB_CLASS_LIST
              ?.at(2)
              ?.job_type_list
              ?.at(5)
              ?.id
          )
        ) {
          categorized_chart_contacts.purchasing_contact_list.push(contact);
        }

        if (
          contact.job_type_id_list.includes(
            JOB_CONSTANTS.JOB_CLASS_LIST
              ?.at(3)
              ?.job_type_list
              ?.at(0)
              ?.id
          )
        ) {
          categorized_chart_contacts.marketing_contact_list.push(contact);
        }

        if (
          contact.job_type_id_list.includes(
            JOB_CONSTANTS.JOB_CLASS_LIST
              ?.at(3)
              ?.job_type_list
              ?.at(1)
              ?.id
          )
        ) {
          categorized_chart_contacts.digital_contact_list.push(contact);
        }

        if (
          contact.job_type_id_list.includes(
            JOB_CONSTANTS.JOB_CLASS_LIST
              ?.at(3)
              ?.job_type_list
              ?.at(2)
              ?.id
          )
        ) {
          categorized_chart_contacts.communication_contact_list.push(contact);
        }

        if (
          contact.job_type_id_list.includes(
            JOB_CONSTANTS.JOB_CLASS_LIST
              ?.at(3)
              ?.job_type_list
              ?.at(3)
              ?.id
          )
        ) {
          categorized_chart_contacts.sales_contact_list.push(contact);
        }

        if (
          contact.job_type_id_list.includes(
            JOB_CONSTANTS.JOB_CLASS_LIST
              ?.at(3)
              ?.job_type_list
              ?.at(4)
              ?.id
          )
        ) {
          categorized_chart_contacts.distribution_contact_list.push(contact);
        }

        if (
          contact.job_type_id_list.includes(
            JOB_CONSTANTS.JOB_CLASS_LIST
              ?.at(3)
              ?.job_type_list
              ?.at(5)
              ?.id
          )
        ) {
          categorized_chart_contacts.artistic_contact_list.push(contact);
        }

        if (
          contact.job_type_id_list.includes(
            JOB_CONSTANTS.JOB_CLASS_LIST
              ?.at(4)
              ?.job_type_list
              ?.at(0)
              ?.id
          )
        ) {
          categorized_chart_contacts.it_contact_list.push(contact);
        }

        if (
          contact.job_type_id_list.includes(
            JOB_CONSTANTS.JOB_CLASS_LIST
              ?.at(4)
              ?.job_type_list
              ?.at(1)
              ?.id
          )
        ) {
          categorized_chart_contacts.research_contact_list.push(contact);
        }

        if (
          contact.job_type_id_list.includes(
            JOB_CONSTANTS.JOB_CLASS_LIST
              ?.at(4)
              ?.job_type_list
              ?.at(2)
              ?.id
          )
        ) {
          categorized_chart_contacts.production_contact_list.push(contact);
        }

        if (
          contact.job_type_id_list.includes(
            JOB_CONSTANTS.JOB_CLASS_LIST
              ?.at(4)
              ?.job_type_list
              ?.at(3)
              ?.id
          )
        ) {
          categorized_chart_contacts.supply_chain_contact_list.push(contact);
        }

        if (
          contact.job_type_id_list.includes(
            JOB_CONSTANTS.JOB_CLASS_LIST
              ?.at(5)
              ?.job_type_list
              ?.at(0)
              ?.id
          )
        ) {
          categorized_chart_contacts.consulting_contact_list.push(contact);
        }

        if (
          contact.job_type_id_list.includes(
            JOB_CONSTANTS.JOB_CLASS_LIST
              ?.at(5)
              ?.job_type_list
              ?.at(1)
              ?.id
          )
        ) {
          categorized_chart_contacts.finance_contact_list.push(contact);
        }

        if (
          contact.job_type_id_list.includes(
            JOB_CONSTANTS.JOB_CLASS_LIST
              ?.at(5)
              ?.job_type_list
              ?.at(2)
              ?.id
          )
        ) {
          categorized_chart_contacts.media_contact_list.push(contact);
        }

        if (
          contact.job_type_id_list.includes(
            JOB_CONSTANTS.JOB_CLASS_LIST
              ?.at(5)
              ?.job_type_list
              ?.at(3)
              ?.id
          )
        ) {
          categorized_chart_contacts.cse_contact_list.push(contact);
        }

        if (
          contact.job_type_id_list.includes(
            JOB_CONSTANTS.JOB_CLASS_LIST
              ?.at(5)
              ?.job_type_list
              ?.at(4)
              ?.id
          )
        ) {
          categorized_chart_contacts.public_service_contact_list.push(contact);
        }

        if (
          contact.job_type_id_list.includes(
            JOB_CONSTANTS.JOB_CLASS_LIST
              ?.at(5)
              ?.job_type_list
              ?.at(5)
              ?.id
          )
        ) {
          categorized_chart_contacts.assistant_contact_list.push(contact);
        }

        if (
          contact.job_type_id_list.includes(
            JOB_CONSTANTS.JOB_CLASS_LIST
              ?.at(6)
              ?.job_type_list
              ?.at(0)
              ?.id
          )
        ) {
          categorized_chart_contacts.executive_committee_contact_list.push(contact);
        }

        if (
          contact.job_type_id_list.includes(
            JOB_CONSTANTS.JOB_CLASS_LIST
              ?.at(6)
              ?.job_type_list
              ?.at(1)
              ?.id
          )
          || contact.job_type_id_list.length === 0
        ) {
          categorized_chart_contacts.other_contact_list.push(contact);
        }

        chart_department_option_list.push(contact.department);
        chart_job_name_option_id_list.push(`${contact.first_name} ${contact.last_name} - ${contact.job_name}`);
      });

    commit('set_categorized_chart_contacts', categorized_chart_contacts);
    commit(
      'set_chart_department_option_list',
      [...new Set(chart_department_option_list
        .map((department) => department.id)
        .filter((department_id) => department_id)),
      ]
        .map((department_id) => chart_department_option_list
          .find((department) => department.id === department_id))
    );
    commit('set_chart_job_name_option_id_list', [...new Set(chart_job_name_option_id_list)]);

    return categorized_chart_contacts;
  },
  async searchChartContacts({ commit, state }) {
    if (state.chart_department_id_list_search.length === 0
      && state.chart_job_name_search.length === 0
    ) {
      commit('set_searched_chart_contacts', state.categorized_chart_contacts);

      return state.categorized_chart_contacts;
    }

    const searched_chart_contacts = {};

    Object.keys(state.categorized_chart_contacts).forEach((key) => {
      searched_chart_contacts[key] = state.categorized_chart_contacts[key].filter((contact) => {
        let match_count = 0;

        if (state.chart_department_id_list_search.length > 0) {
          if (state.chart_department_id_list_search.includes(contact.department.id)) {
            match_count += 1;
          }
        }

        if (state.chart_job_name_search.length > 0) {
          if (state.chart_job_name_search.includes(contact.job_name)) {
            match_count += 1;
          }
        }

        if (match_count > 0) {
          return true;
        }

        return false;
      });
    });

    commit('set_searched_chart_contacts', searched_chart_contacts);

    return searched_chart_contacts;
  },
  async getActiveAccounts(_, input) {
    const result = await search_repository.getActiveAccounts(input);

    return result?.account_list || [];
  },
  async getPressNews(_, input) {
    const result = await search_repository.getPressNews(input);

    return result?.news_list || [];
  },
  async getBusinessOpportunityNews(_, input) {
    const result = await search_repository.getBusinessOpportunityNews(input);

    return result?.news_list || [];
  },
  async getActiveContacts(_, input) {
    const result = await search_repository.getContactsActive(input);

    return result?.contact_list || [];
  },
};

const mutations = {
  set_account(state, account) {
    state.account = account;
  },
  set_account_is_inactive(state, account_is_inactive) {
    state.account_is_inactive = account_is_inactive;
  },
  set_account_with_aggregated_data(state, account_with_aggregated_data) {
    state.account_with_aggregated_data = account_with_aggregated_data;
  },
  set_categorized_chart_contacts(state, categorized_chart_contacts) {
    state.categorized_chart_contacts = categorized_chart_contacts;
  },
  set_chart_contact_list(state, chart_contact_list) {
    state.chart_contact_list = chart_contact_list;
  },
  set_chart_department_id_list_search(state, chart_department_id_list_search) {
    state.chart_department_id_list_search = chart_department_id_list_search;
  },
  set_chart_department_option_list(state, chart_department_option_list) {
    state.chart_department_option_list = chart_department_option_list;
  },
  set_chart_job_name_option_id_list(state, chart_job_name_option_id_list) {
    state.chart_job_name_option_id_list = chart_job_name_option_id_list;
  },
  set_chart_job_name_search(state, chart_job_name_search) {
    state.chart_job_name_search = chart_job_name_search;
  },
  set_chart_view(state, chart_view) {
    state.chart_view = chart_view;
  },
  set_latest_news_list(state, latest_news_list) {
    state.latest_news_list = latest_news_list;
  },
  set_loading_search_news_with_criteria(state, loading_search_news_with_criteria) {
    state.loading_search_news_with_criteria = loading_search_news_with_criteria;
  },
  set_most_recent_chart_contact_list(state, most_recent_chart_contact_list) {
    state.most_recent_chart_contact_list = most_recent_chart_contact_list;
  },
  set_news_criteria_limit(state, news_criteria_limit) {
    state.news_criteria_limit = news_criteria_limit;
  },
  set_news_criteria_offset(state, news_criteria_offset) {
    state.news_criteria_offset = news_criteria_offset;
  },
  set_news_criteria_publication_date_max(state, news_criteria_publication_date_max) {
    state.news_criteria_publication_date_max = news_criteria_publication_date_max;
  },
  set_news_criteria_publication_date_min(state, news_criteria_publication_date_min) {
    state.news_criteria_publication_date_min = news_criteria_publication_date_min;
  },
  set_news_criteria_quick_search(state, news_criteria_quick_search) {
    state.news_criteria_quick_search = news_criteria_quick_search;
  },
  set_news_criteria_tag_id_list(state, news_criteria_tag_id_list) {
    state.news_criteria_tag_id_list = news_criteria_tag_id_list;
  },
  set_news_list_with_criteria(state, news_list_with_criteria) {
    state.news_list_with_criteria = news_list_with_criteria;
  },
  set_primary_tag_list(state, primary_tag_list) {
    state.primary_tag_list = primary_tag_list;
  },
  set_reach_end_search_news_with_criteria(state, reach_end_search_news_with_criteria) {
    state.reach_end_search_news_with_criteria = reach_end_search_news_with_criteria;
  },
  set_searched_chart_contacts(state, searched_chart_contacts) {
    state.searched_chart_contacts = searched_chart_contacts;
  },
  set_secondary_tag_list(state, secondary_tag_list) {
    state.secondary_tag_list = secondary_tag_list;
  },
  set_smart_score_for_account(state, smart_score_for_account) {
    state.smart_score_for_account = smart_score_for_account;
  },
};

export default {
  actions,
  getters,
  mutations,
  namespaced: true,
  state,
};
